
import payrollService from './payrollService';

// PNG Employment Act 1978 Required Clauses by Contract Type
const REQUIRED_CLAUSES = {
  full_time: [
    { id: 'position_duties', name: 'Position and Duties', description: 'Clear scope of work and reporting lines', severity: 'critical' },
    { id: 'hours_overtime', name: 'Hours and Overtime', description: 'Standard hours, overtime rules, shift penalties', severity: 'critical' },
    { id: 'annual_leave', name: 'Annual Leave', description: 'Minimum 20 days annual leave entitlement', severity: 'critical' },
    { id: 'sick_leave', name: 'Sick Leave', description: 'Minimum 10 days sick leave entitlement', severity: 'critical' },
    { id: 'maternity_leave', name: 'Maternity Leave', description: 'Statutory maternity leave provisions', severity: 'high' },
    { id: 'long_service_leave', name: 'Long Service Leave', description: 'Long service leave entitlements', severity: 'high' },
    { id: 'remuneration', name: 'Remuneration', description: 'Base salary, allowances, superannuation, tax deductions', severity: 'critical' },
    { id: 'superannuation', name: 'Superannuation (Nasfund)', description: 'Nasfund contribution details and rates', severity: 'critical' },
    { id: 'termination_notice', name: 'Termination & Notice', description: 'Notice periods (minimum 4 weeks for full-time)', severity: 'critical' },
    { id: 'discipline_procedures', name: 'Discipline Procedures', description: 'Misconduct processes and disciplinary actions', severity: 'high' },
    { id: 'minimum_wage_compliance', name: 'Minimum Wage Compliance', description: 'Wage rate meets or exceeds K5.00/hour minimum', severity: 'critical' }
  ],
  casual: [
    { id: 'engagement_basis', name: 'Engagement Basis', description: 'Irregular hours, no guarantee of ongoing work', severity: 'critical' },
    { id: 'hourly_wage_rate', name: 'Hourly Wage Rate', description: 'Hourly rate at/above K5.00 minimum wage', severity: 'critical' },
    { id: 'overtime_basis', name: 'Overtime Basis', description: 'Overtime calculation and payment terms', severity: 'high' },
    { id: 'public_holiday_work', name: 'Public Holiday Work', description: 'Provisions for public holiday work and rates', severity: 'high' },
    { id: 'short_notice_periods', name: 'Short Notice Periods', description: 'Appropriate notice periods for casual work', severity: 'medium' },
    { id: 'statutory_entitlements', name: 'Statutory Entitlements', description: 'Applicable leave if qualifying', severity: 'medium' },
    { id: 'minimum_wage_compliance', name: 'Minimum Wage Compliance', description: 'Wage rate meets or exceeds K5.00/hour minimum', severity: 'critical' }
  ],
  fixed_term: [
    { id: 'term_length', name: 'Term Length', description: 'Contract duration and end date', severity: 'critical' },
    { id: 'project_deliverables', name: 'Project Deliverables', description: 'Clear project KPIs and deliverables', severity: 'critical' },
    { id: 'renewal_conditions', name: 'Renewal Conditions', description: 'Terms for contract renewal or extension', severity: 'high' },
    { id: 'leave_treatment', name: 'Leave Treatment', description: 'Leave entitlement treatment across fixed term', severity: 'high' },
    { id: 'end_of_term_procedures', name: 'End-of-Term Procedures', description: 'Separation procedures and payouts', severity: 'critical' },
    { id: 'remuneration', name: 'Remuneration', description: 'Base salary, allowances, superannuation', severity: 'critical' },
    { id: 'minimum_wage_compliance', name: 'Minimum Wage Compliance', description: 'Wage rate meets or exceeds K5.00/hour minimum', severity: 'critical' }
  ],
  non_citizen: [
    { id: 'work_permit_details', name: 'Work Permit Details', description: 'Permit number, validity dates, role alignment', severity: 'critical' },
    { id: 'localization_commitment', name: 'Localization & Training', description: 'Commitments to train PNG nationals', severity: 'critical' },
    { id: 'permit_contingencies', name: 'Permit Contingencies', description: 'Employment continuity tied to permit status', severity: 'critical' },
    { id: 'dlir_escalation', name: 'DLIR Escalation Contacts', description: 'DLIR oversight and contact information', severity: 'high' },
    { id: 'termination_permit', name: 'Termination & Permit', description: 'Separation procedures linked to permit status', severity: 'high' },
    { id: 'remuneration', name: 'Remuneration', description: 'Base salary, allowances, superannuation', severity: 'critical' },
    { id: 'minimum_wage_compliance', name: 'Minimum Wage Compliance', description: 'Wage rate meets or exceeds K5.00/hour minimum', severity: 'critical' }
  ]
};

// Compliance improvement suggestions
const IMPROVEMENT_SUGGESTIONS = {
  position_duties: 'Add detailed job description with clear reporting structure and key responsibilities aligned with Employment Act 1978 standards.',
  hours_overtime: 'Specify standard working hours (typically 40-44 hours/week) and overtime rates (minimum 1.5x for overtime, 2x for public holidays).',
  annual_leave: 'Include minimum 20 days annual leave per year as per Employment Act 1978, with accrual and carryover provisions.',
  sick_leave: 'Include minimum 10 days sick leave per year with medical certificate requirements for absences over 2 consecutive days.',
  maternity_leave: 'Add statutory maternity leave provisions (typically 6 weeks) with job protection guarantees.',
  long_service_leave: 'Include long service leave entitlements after qualifying service period (typically 10 years).',
  remuneration: 'Detail base salary/wage, payment frequency, allowances (transport, meal, site), and deduction authorization process.',
  superannuation: 'Specify Nasfund contribution rates (typically 6% employee + 8.4% employer) with member registration details.',
  termination_notice: 'Include minimum notice periods: 4 weeks for full-time employees, with provisions for payment in lieu of notice.',
  discipline_procedures: 'Add progressive discipline process: verbal warning → written warning → final warning → termination, with right to respond.',
  engagement_basis: 'Clearly state casual engagement terms: no guaranteed hours, engagement on as-needed basis, right to refuse work.',
  hourly_wage_rate: 'Specify hourly rate meeting or exceeding current minimum wage (K5.00/hour effective 2026-01-01).',
  overtime_basis: 'Define overtime calculation: hours beyond standard hours paid at minimum 1.5x hourly rate.',
  public_holiday_work: 'Include public holiday provisions: 2x rate for work on public holidays, or alternative day off with pay.',
  short_notice_periods: 'Specify appropriate notice periods for casual work (typically 1 hour to 1 day depending on engagement pattern).',
  statutory_entitlements: 'Include provisions for leave entitlements if casual employee qualifies (regular and systematic engagement).',
  term_length: 'Clearly state contract start and end dates, total duration, and conditions triggering early termination.',
  project_deliverables: 'Define specific project outcomes, KPIs, milestones, and performance evaluation criteria.',
  renewal_conditions: 'Specify conditions for contract renewal, notice period for renewal decision, and maximum consecutive terms.',
  leave_treatment: 'Detail how annual and sick leave accrues during fixed term, and pro-rata payout on contract end.',
  end_of_term_procedures: 'Include end-of-contract procedures: final pay calculation, leave payouts, return of property, exit interview.',
  work_permit_details: 'Include work permit number, issue and expiry dates, permitted role/occupation, and renewal obligations.',
  localization_commitment: 'Specify training commitments for PNG nationals, knowledge transfer requirements, and localization timeline.',
  permit_contingencies: 'State that employment is contingent on valid work permit; termination procedures if permit expires or is cancelled.',
  dlir_escalation: 'Include DLIR contact information for permit queries and compliance escalation (phone, email, regional office).',
  termination_permit: 'Detail separation procedures linked to permit status, repatriation arrangements, and final settlement.',
  minimum_wage_compliance: 'Ensure wage/salary rate meets or exceeds K5.00/hour minimum wage (effective 2026-01-01). For salaried employees, verify annual salary ÷ (52 weeks × standard hours) ≥ K5.00.'
};

export const contractComplianceService = {
  /**
   * Scan contract for PNG Employment Act 1978 compliance
   * @param {Object} contract - Contract object with contractType, contractClauses, hourlyRate, baseSalary, standardHours
   * @returns {Object} Compliance scan results with violations and suggestions
   */
  async scanContract(contract) {
    try {
      const contractType = contract?.contractType;
      const contractClauses = contract?.contractClauses || {};
      const hourlyRate = contract?.hourlyRate;
      const baseSalary = contract?.baseSalary;
      const standardHours = contract?.standardHours || 40;

      // Get required clauses for this contract type
      const requiredClauses = REQUIRED_CLAUSES?.[contractType] || [];

      // Get current minimum wage
      const minimumWage = await payrollService?.getCurrentMinimumWage();
      const currentMinWage = minimumWage?.hourlyRate || 5.0;

      // Check for missing provisions
      const missingProvisions = [];
      const presentProvisions = [];
      const violations = [];

      for (const requiredClause of requiredClauses) {
        const clausePresent = contractClauses?.[requiredClause?.id] === true || 
                             contractClauses?.[requiredClause?.id] === 'included' ||
                             (typeof contractClauses?.[requiredClause?.id] === 'string' && contractClauses?.[requiredClause?.id]?.length > 0);

        if (!clausePresent) {
          missingProvisions?.push({
            clauseId: requiredClause?.id,
            clauseName: requiredClause?.name,
            description: requiredClause?.description,
            severity: requiredClause?.severity,
            suggestion: IMPROVEMENT_SUGGESTIONS?.[requiredClause?.id] || 'Add this clause to ensure compliance with PNG Employment Act 1978.'
          });

          violations?.push({
            id: `missing_${requiredClause?.id}`,
            title: `Missing: ${requiredClause?.name}`,
            description: requiredClause?.description,
            severity: requiredClause?.severity,
            category: 'missing_clause',
            suggestion: IMPROVEMENT_SUGGESTIONS?.[requiredClause?.id]
          });
        } else {
          presentProvisions?.push({
            clauseId: requiredClause?.id,
            clauseName: requiredClause?.name,
            status: 'compliant'
          });
        }
      }

      // Check minimum wage compliance
      let wageCompliant = true;
      let wageViolation = null;

      if (contractType === 'casual' || hourlyRate) {
        // Hourly rate check
        if (hourlyRate && hourlyRate < currentMinWage) {
          wageCompliant = false;
          wageViolation = {
            id: 'minimum_wage_violation',
            title: 'Minimum Wage Violation',
            description: `Hourly rate K${hourlyRate?.toFixed(2)} is below minimum wage K${currentMinWage?.toFixed(2)}/hour`,
            severity: 'critical',
            category: 'wage_violation',
            suggestion: `Increase hourly rate to at least K${currentMinWage?.toFixed(2)}/hour to comply with current minimum wage determination (effective ${minimumWage?.effectiveDate}).`
          };
          violations?.push(wageViolation);
        }
      } else if (baseSalary && standardHours) {
        // Salaried employee - calculate effective hourly rate
        const weeksPerYear = 52;
        const effectiveHourlyRate = baseSalary / (weeksPerYear * standardHours);
        
        if (effectiveHourlyRate < currentMinWage) {
          wageCompliant = false;
          wageViolation = {
            id: 'minimum_wage_violation',
            title: 'Minimum Wage Violation (Salaried)',
            description: `Effective hourly rate K${effectiveHourlyRate?.toFixed(2)} (K${baseSalary?.toFixed(2)}/year ÷ ${weeksPerYear} weeks ÷ ${standardHours} hours) is below minimum wage K${currentMinWage?.toFixed(2)}/hour`,
            severity: 'critical',
            category: 'wage_violation',
            suggestion: `Increase annual salary to at least K${(currentMinWage * standardHours * weeksPerYear)?.toFixed(2)} to comply with minimum wage (K${currentMinWage?.toFixed(2)}/hour × ${standardHours} hours × ${weeksPerYear} weeks).`
          };
          violations?.push(wageViolation);
        }
      }

      // Calculate compliance score
      const totalRequiredClauses = requiredClauses?.length;
      const presentClausesCount = presentProvisions?.length;
      const complianceScore = totalRequiredClauses > 0 
        ? Math.round((presentClausesCount / totalRequiredClauses) * 100)
        : 0;

      // Determine overall compliance status
      let complianceStatus = 'compliant';
      const criticalViolations = violations?.filter(v => v?.severity === 'critical')?.length || 0;
      const highViolations = violations?.filter(v => v?.severity === 'high')?.length || 0;

      if (criticalViolations > 0 || !wageCompliant) {
        complianceStatus = 'non_compliant';
      } else if (highViolations > 0 || complianceScore < 80) {
        complianceStatus = 'needs_improvement';
      }

      return {
        complianceStatus,
        complianceScore,
        wageCompliant,
        minimumWageRate: currentMinWage,
        minimumWageEffectiveDate: minimumWage?.effectiveDate,
        totalRequiredClauses,
        presentClausesCount,
        missingClausesCount: missingProvisions?.length,
        criticalViolations,
        highViolations,
        missingProvisions,
        presentProvisions,
        violations,
        scannedAt: new Date()?.toISOString(),
        contractType
      };
    } catch (error) {
      throw new Error(`Contract scan failed: ${error?.message}`);
    }
  },

  /**
   * Get required clauses for a specific contract type
   * @param {string} contractType - Contract type (full_time, casual, fixed_term, non_citizen)
   * @returns {Array} Required clauses for the contract type
   */
  getRequiredClauses(contractType) {
    return REQUIRED_CLAUSES?.[contractType] || [];
  },

  /**
   * Get improvement suggestion for a specific clause
   * @param {string} clauseId - Clause identifier
   * @returns {string} Improvement suggestion
   */
  getImprovementSuggestion(clauseId) {
    return IMPROVEMENT_SUGGESTIONS?.[clauseId] || 'Add this clause to ensure compliance.';
  },

  /**
   * Validate minimum wage compliance
   * @param {number} rate - Hourly or effective hourly rate
   * @returns {Object} Validation result
   */
  async validateMinimumWage(rate) {
    try {
      const minimumWage = await payrollService?.getCurrentMinimumWage();
      const currentMinWage = minimumWage?.hourlyRate || 5.0;

      return {
        compliant: rate >= currentMinWage,
        providedRate: rate,
        minimumRate: currentMinWage,
        effectiveDate: minimumWage?.effectiveDate,
        difference: rate - currentMinWage
      };
    } catch (error) {
      throw new Error(`Minimum wage validation failed: ${error?.message}`);
    }
  }
};

export default contractComplianceService;
